import { useEffect, useMemo, useState } from 'react';
import { httpErrorStatus, isNestMissingRouteError } from 'features/common/helpers/httpErrors';
import { MERCHANT_NUMBER_RULES, MerchantNumberRule } from '../constants/merchantNumbers';
import { activeRulesFrom, merchantRulesOf } from './rules';
import { getBaseCarrierRules } from './services';
import { BaseCarrierRule } from './types';

// ─── Las reglas por aerolínea: la base, con la tabla fija de respaldo ────────
// Lo que se carga en Mesa de cruces → Reglas de cruce (`GET /rules`) es la
// fuente del modal de autorización y de "Códigos de comercio y cuotas". Si la
// Support API no las da, la tabla fija del Power (`MERCHANT_NUMBER_RULES`): el
// modal nunca deja de andar. Un solo pedido a la vez, guardado en el módulo por
// 5 minutos y compartido entre el modal y la página; un alta, una edición o una
// baja en la Mesa (`CarrierRules`) lo invalida.

/** Cuánto se espera a `/rules` antes de usar la tabla fija. */
export const MERCHANT_RULES_WAIT_MS = 5_000;

/** Cuánto vale lo leído: pasado eso, abrir el modal (o entrar a la página) lo vuelve a pedir. */
export const MERCHANT_RULES_FRESH_MS = 5 * 60_000;

/** Un pedido sin respuesta en este tiempo se da por caído: libera el lugar y la próxima apertura vuelve a probar. */
export const MERCHANT_RULES_TIMEOUT_MS = 10_000;

/**
 * De dónde salen las reglas:
 *  - `loading`: se están pidiendo y todavía no pasaron 5 s (no hay ninguna);
 *  - `base`: las de la base;
 *  - `slow`: tardan más de 5 s y, mientras, va la tabla fija;
 *  - `off`: el módulo está apagado (404 de una ruta que no existe): la tabla fija, como siempre;
 *  - `empty`: la base no tiene reglas vigentes: la tabla fija;
 *  - `denied`: la API no deja leerlas a este usuario (403: sin la agencia 1 y sin rol admin): la tabla fija;
 *  - `fixed`: la Support API no respondió (red, 5xx, 10 s sin respuesta) o respondió algo que no se le puede creer
 *    (incompleto o de otra forma): la tabla fija.
 */
export type MerchantRulesSource = 'loading' | 'base' | 'slow' | 'off' | 'empty' | 'denied' | 'fixed';

/**
 * Lo que respondió `/rules`: las reglas vigentes (`null` si no se le puede creer) o, si falló, si fue por el módulo
 * apagado (`off`) o porque el usuario no puede leerlas (`denied`).
 */
export interface MerchantRulesRead {
	rules: BaseCarrierRule[] | null;
	off: boolean;
	denied: boolean;
}

let saved: { read: MerchantRulesRead; at: number } | null = null;
let inFlight: Promise<MerchantRulesRead> | null = null;
/** Sube con cada invalidación: lo que llega de un pedido de antes no se guarda. */
let generation = 0;

/** Lo guardado, si tiene menos de 5 minutos; un 403, mientras dure la sesión (no cambia de una apertura a otra). */
export function cachedMerchantRules(): MerchantRulesRead | null {
	if (!saved) return null;
	return saved.read.denied || Date.now() - saved.at < MERCHANT_RULES_FRESH_MS ? saved.read : null;
}

/**
 * Las reglas: las guardadas si valen; si no, el pedido que ya esté en vuelo o uno nuevo, que a los 10 s se da por
 * caído. Nunca falla: un error es una lectura sin reglas. Se guardan las reglas que se le creen (también una base sin
 * reglas vigentes), el módulo apagado y un 403. Una respuesta que no se entiende, un error de red o de la API y un
 * pedido sin respuesta, no: la próxima apertura vuelve a probar.
 */
export function loadMerchantRules(): Promise<MerchantRulesRead> {
	const fresh = cachedMerchantRules();
	if (fresh) return Promise.resolve(fresh);
	if (inFlight) return inFlight;
	const mine = generation;
	let timer: number | undefined;
	const hung = new Promise<never>((_, reject) => {
		timer = window.setTimeout(() => reject(new Error('GET /rules sin respuesta')), MERCHANT_RULES_TIMEOUT_MS);
	});
	// Siempre las de la agencia 1, de cualquier país (`getBaseCarrierRules`): la tabla fija es la suya y la usan todos.
	const request = Promise.race([getBaseCarrierRules(), hung])
		.then((data): MerchantRulesRead => ({ rules: activeRulesFrom(data), off: false, denied: false }))
		.catch(
			(err): MerchantRulesRead => ({
				rules: null,
				off: isNestMissingRouteError(err),
				denied: httpErrorStatus(err) === 403,
			}),
		)
		.then((read) => {
			window.clearTimeout(timer);
			if (mine === generation) {
				inFlight = null;
				if (read.rules || read.off || read.denied) saved = { read, at: Date.now() };
			}
			return read;
		});
	inFlight = request;
	return request;
}

/** Cambiaron las reglas en la Mesa: lo guardado (y lo que esté en vuelo) ya no vale. */
export function invalidateMerchantRules(): void {
	generation += 1;
	saved = null;
	inFlight = null;
}

export function merchantRulesSource(read: MerchantRulesRead | null, slow: boolean): MerchantRulesSource {
	if (!read) return slow ? 'slow' : 'loading';
	if (read.rules?.length) return 'base';
	if (read.rules) return 'empty';
	if (read.denied) return 'denied';
	return read.off ? 'off' : 'fixed';
}

interface RulesState {
	read: MerchantRulesRead | null;
	slow: boolean;
}

const NOTHING: RulesState = { read: null, slow: false };

/**
 * Lo leído de `/rules` mientras `active` (el modal abierto; la página, siempre): lo guardado, de una, o lo que traiga
 * `loadMerchantRules`, con `slow` a los 5 s sin respuesta. Con `keepFallback` (el modal), si a los 5 s se pasó a la
 * tabla fija, queda ella hasta cerrar: no cambia un código de comercio que ya se vio. Sin él (la página), cuando llegan
 * las de la base, las reemplazan.
 */
export function useMerchantRulesRead(active: boolean, keepFallback = false): RulesState {
	const [state, setState] = useState<RulesState>(NOTHING);

	useEffect(() => {
		if (!active) {
			setState(NOTHING);
			return undefined;
		}
		const fresh = cachedMerchantRules();
		if (fresh) {
			setState({ read: fresh, slow: false });
			return undefined;
		}
		let alive = true;
		setState(NOTHING);
		const timer = setTimeout(() => setState((now) => ({ ...now, slow: true })), MERCHANT_RULES_WAIT_MS);
		loadMerchantRules().then((read) => {
			clearTimeout(timer);
			if (alive) setState((now) => (keepFallback && now.slow ? now : { read, slow: false }));
		});
		return () => {
			alive = false;
			clearTimeout(timer);
		};
	}, [active, keepFallback]);

	// Con lo guardado vigente no hay un primer dibujo "cargando" antes del efecto.
	if (active && !state.read && !state.slow) {
		const fresh = cachedMerchantRules();
		if (fresh) return { read: fresh, slow: false };
	}
	return state;
}

const NONE: MerchantNumberRule[] = [];

/**
 * Las reglas del modal de autorización, pedidas al abrirlo (`active`): las de la base (`merchantRulesOf`) o, si la
 * Support API no las da, la tabla fija. Cargando (`loading`, hasta 5 s), ninguna: el modal no muestra un código de
 * comercio que después cambie.
 */
export function useMerchantRules(active = true): { rules: MerchantNumberRule[]; source: MerchantRulesSource } {
	const { read, slow } = useMerchantRulesRead(active, true);
	const source = merchantRulesSource(read, slow);
	const base = useMemo(() => (read?.rules?.length ? merchantRulesOf(read.rules) : null), [read]);
	if (source === 'loading') return { rules: NONE, source };
	return { rules: base ?? MERCHANT_NUMBER_RULES, source };
}
